import { useContext, useMemo } from 'react';
import {
  CustomModalEditWidgetHeader,
  SelectorOption
} from '@Components/ModalEditWidget/modal-edit-constants';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import { MdCompareArrows, MdVerticalAlignTop, MdSettings } from 'react-icons/md';
import { Form } from 'react-bootstrap';
import { applyTableChanges, withIndexes } from '@src/utils/optimization-utils';
import {
  HroasBoundConstraint as BoundConstraint,
  HroasCapConstraint as CapConstraint,
  HroasGlobalSettings as GlobalConstraints
} from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { OptimizationsContext } from './optimizations-constants';

import './ConstraintView.scss';

interface GlobalDataRow {
  name: string;
  key: string;
  value: number;
  index?: number;
}

type ValidationFunction = (item: any, handleInvalidMessage?: (msg: string) => void) => boolean;

export const ConstraintView = () => {
  const {
    constraintData,
    constraintCandidates,
    setConstraintData: updateConstraintData,
    savingConstraint
  } = useContext(OptimizationsContext);

  const selectorOptions: Record<string, SelectorOption[]> = useMemo(() => {
    const dgFeatures = (constraintCandidates?.dgFeatures || []).map(feature => ({
      label: feature,
      value: feature
    }));
    const dcFeatures = (constraintCandidates?.dcFeatures || []).map(feature => ({
      label: feature,
      value: feature
    }));

    return {
      dgFeatures,
      dcFeatures
    };
  }, [constraintCandidates]);

  const checkBoundAbsValid: ValidationFunction = (bound, handleInvalidMessage) => {
    if (
      bound?.feature &&
      constraintData?.constraints?.dg_share_bounds?.some(b => b.feature === bound.feature)
    ) {
      handleInvalidMessage?.(`Feature "${bound.feature}" already exists in DG Share bounds`);
      return false;
    }
    if (!(bound?.feature?.length > 0)) {
      handleInvalidMessage?.('Feature is required');
      return false;
    }
    if (bound?.minValue == null && bound?.maxValue == null) {
      handleInvalidMessage?.('Must specify both min and max values');
      return false;
    }
    if (bound?.minValue == null) {
      handleInvalidMessage?.('Min value is required');
      return false;
    }
    if (bound?.maxValue == null) {
      handleInvalidMessage?.('Max value is required');
      return false;
    }
    const minValue = parseFloat(bound.minValue);
    const maxValue = parseFloat(bound.maxValue);
    if (minValue < 0) {
      handleInvalidMessage?.('Min value must be greater than or equal to 0');
      return false;
    }
    if (maxValue < 0) {
      handleInvalidMessage?.('Max value must be greater than or equal to 0');
      return false;
    }
    if (minValue > maxValue) {
      handleInvalidMessage?.('Min value must not be greater than max value');
      return false;
    }
    return true;
  };

  const checkBoundShareValid: ValidationFunction = (bound, handleInvalidMessage) => {
    if (
      bound?.feature &&
      constraintData?.constraints?.dg_abs_bounds?.some(b => b.feature === bound.feature)
    ) {
      handleInvalidMessage?.(`Feature "${bound.feature}" already exists in DG Absolute bounds`);
      return false;
    }
    if (!(bound?.feature?.length > 0)) {
      handleInvalidMessage?.('Feature is required');
      return false;
    }
    if (bound?.minValue == null && bound?.maxValue == null) {
      handleInvalidMessage?.('Must specify both min and max values');
      return false;
    }
    if (bound?.minValue == null) {
      handleInvalidMessage?.('Min value is required');
      return false;
    }
    if (bound?.maxValue == null) {
      handleInvalidMessage?.('Max value is required');
      return false;
    }
    const minValue = parseFloat(bound.minValue);
    const maxValue = parseFloat(bound.maxValue);
    if (minValue < 0 || minValue > 1) {
      handleInvalidMessage?.('Min value must be between 0 and 1');
      return false;
    }
    if (maxValue < 0 || maxValue > 1) {
      handleInvalidMessage?.('Max value must be between 0 and 1');
      return false;
    }
    if (minValue > maxValue) {
      handleInvalidMessage?.('Min value must not be greater than max value');
      return false;
    }
    return true;
  };

  const checkCapAbsValid: ValidationFunction = (cap, handleInvalidMessage) => {
    if (
      cap?.feature &&
      constraintData?.constraints?.dc_share_caps?.some(c => c.feature === cap.feature)
    ) {
      handleInvalidMessage?.(`Feature "${cap.feature}" already exists in DC Share caps`);
      return false;
    }
    if (!(cap?.feature?.length > 0)) {
      handleInvalidMessage?.('Feature is required');
      return false;
    }
    if (cap?.value == null) {
      handleInvalidMessage?.('Value is required');
      return false;
    }
    const value = parseFloat(cap.value);
    if (value < 0) {
      handleInvalidMessage?.('Value must be greater than or equal to 0');
      return false;
    }
    return true;
  };

  const checkCapShareValid: ValidationFunction = (cap, handleInvalidMessage) => {
    if (
      cap?.feature &&
      constraintData?.constraints?.dc_abs_caps?.some(c => c.feature === cap.feature)
    ) {
      handleInvalidMessage?.(`Feature "${cap.feature}" already exists in DC Absolute caps`);
      return false;
    }
    if (!(cap?.feature?.length > 0)) {
      handleInvalidMessage?.('Feature is required');
      return false;
    }
    if (cap?.value == null) {
      handleInvalidMessage?.('Value is required');
      return false;
    }
    const value = parseFloat(cap.value);
    if (value < 0 || value > 1) {
      handleInvalidMessage?.('Value must be between 0 and 1');
      return false;
    }
    return true;
  };

  // Header definitions for each constraint type
  const dgAbsBoundsHeaders = useMemo<CustomModalEditWidgetHeader[]>(
    () => [
      {
        type: 'select',
        field: 'feature',
        label: 'Feature',
        flex: 2,
        modalRow: 0,
        modalFlex: 1,
        options: 'dgFeatures'
      },
      {
        type: 'decimal',
        field: 'minValue',
        label: 'Min Value',
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        min: 0,
        step: 1
      },
      {
        type: 'decimal',
        field: 'maxValue',
        label: 'Max Value',
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        min: 0,
        step: 1
      }
    ],
    []
  );

  const dgShareBoundsHeaders = useMemo<CustomModalEditWidgetHeader[]>(
    () => [
      {
        type: 'select',
        field: 'feature',
        label: 'Feature',
        flex: 2,
        modalRow: 0,
        modalFlex: 1,
        options: 'dgFeatures'
      },
      {
        type: 'decimal',
        field: 'minValue',
        label: 'Min Value (0-1)',
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        min: 0,
        max: 1,
        step: 0.01
      },
      {
        type: 'decimal',
        field: 'maxValue',
        label: 'Max Value (0-1)',
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        min: 0,
        max: 1,
        step: 0.01
      }
    ],
    []
  );

  const dcAbsCapsHeaders = useMemo<CustomModalEditWidgetHeader[]>(
    () => [
      {
        type: 'select',
        field: 'feature',
        label: 'Feature',
        flex: 2,
        modalRow: 0,
        modalFlex: 1,
        options: 'dcFeatures'
      },
      {
        type: 'decimal',
        field: 'value',
        label: 'Cap Value',
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        min: 0,
        step: 1
      }
    ],
    []
  );

  const dcShareCapsHeaders = useMemo<CustomModalEditWidgetHeader[]>(
    () => [
      {
        type: 'select',
        field: 'feature',
        label: 'Feature',
        flex: 2,
        modalRow: 0,
        modalFlex: 1,
        options: 'dcFeatures'
      },
      {
        type: 'decimal',
        field: 'value',
        label: 'Cap Value (0-1)',
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        min: 0,
        max: 1,
        step: 0.01
      }
    ],
    []
  );

  const globalHeaders = useMemo<CustomModalEditWidgetHeader[]>(
    () => [
      {
        label: 'Setting',
        field: 'name',
        flex: 2,
        readOnly: true,
        modalRow: 0,
        modalFlex: 2
      },
      {
        label: 'Weight',
        field: 'value',
        type: 'decimal',
        flex: 1,
        modalRow: 0,
        modalFlex: 1
      }
    ],
    []
  );

  const globalData = useMemo<GlobalDataRow[]>(() => {
    if (!constraintData) {
      return [];
    }
    return [
      {
        name: 'rMMM Weight',
        key: 'r_mmm_weight',
        value: constraintData.constraints.global.r_mmm_weight,
        index: 0
      },
      {
        name: 'Geo MMM Weight',
        key: 'geo_mmm_weight',
        value: constraintData.constraints.global.geo_mmm_weight,
        index: 1
      },
      {
        name: 'Brand Equity Weight',
        key: 'brand_equity_weight',
        value: constraintData.constraints.global.brand_equity_weight,
        index: 2
      }
    ];
  }, [constraintData]);

  if (!constraintData) {
    return <FullPageSpinner />;
  }

  return (
    <div className='constraint-view-body'>
      {/* Total Budget */}
      <div className='overall-budget-container'>
        <div className='table-header'>
          <div className='table-name'>Total Budget</div>
        </div>
        <Form.Control
          className='overall-budget'
          type='number'
          min={0}
          disabled={savingConstraint}
          value={constraintData.constraints.total}
          onChange={e => {
            const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
            updateConstraintData({
              ...constraintData,
              constraints: {
                ...constraintData.constraints,
                total: Number.isNaN(value) ? 0 : value
              }
            });
          }}
        />
      </div>

      <div className='constraint-view'>
        {/* DG Absolute Bounds */}
        <ModalEditWidget<BoundConstraint>
          saveOnEdit
          saveButtonText='Apply'
          readOnly={savingConstraint}
          className='constraint-table'
          filterBar={false}
          headers={dgAbsBoundsHeaders}
          name={
            <span style={{ color: '#7e57c2' }}>
              <MdCompareArrows fill='#7e57c2' size='22' style={{ marginTop: '-2px' }} />
              &nbsp;&nbsp;DG Absolute Bounds
            </span>
          }
          initialTableData={withIndexes(constraintData.constraints.dg_abs_bounds)}
          onSaveModal={({ addedData, modifiedData, deletedData }) => {
            updateConstraintData({
              ...constraintData,
              constraints: {
                ...constraintData.constraints,
                dg_abs_bounds: applyTableChanges(
                  withIndexes(constraintData.constraints.dg_abs_bounds),
                  addedData,
                  modifiedData,
                  deletedData
                )
              }
            });
          }}
          checkIsValid={checkBoundAbsValid}
          selectorOptions={selectorOptions}
        />

        {/* DG Share Bounds */}
        <ModalEditWidget<BoundConstraint>
          saveOnEdit
          saveButtonText='Apply'
          readOnly={savingConstraint}
          className='constraint-table'
          filterBar={false}
          headers={dgShareBoundsHeaders}
          name={
            <span style={{ color: '#7e57c2' }}>
              <MdCompareArrows fill='#7e57c2' size='22' style={{ marginTop: '-2px' }} />
              &nbsp;&nbsp;DG Share Bounds
            </span>
          }
          initialTableData={withIndexes(constraintData.constraints.dg_share_bounds)}
          onSaveModal={({ addedData, modifiedData, deletedData }) => {
            updateConstraintData({
              ...constraintData,
              constraints: {
                ...constraintData.constraints,
                dg_share_bounds: applyTableChanges(
                  withIndexes(constraintData.constraints.dg_share_bounds),
                  addedData,
                  modifiedData,
                  deletedData
                )
              }
            });
          }}
          checkIsValid={checkBoundShareValid}
          selectorOptions={selectorOptions}
        />

        {/* DC Absolute Caps */}
        <ModalEditWidget<CapConstraint>
          saveOnEdit
          saveButtonText='Apply'
          readOnly={savingConstraint}
          className='constraint-table'
          filterBar={false}
          headers={dcAbsCapsHeaders}
          name={
            <span style={{ color: '#7e57c2' }}>
              <MdVerticalAlignTop fill='#7e57c2' size='22' style={{ marginTop: '-2px' }} />
              &nbsp;&nbsp;DC Absolute Caps
            </span>
          }
          initialTableData={withIndexes(constraintData.constraints.dc_abs_caps)}
          onSaveModal={({ addedData, modifiedData, deletedData }) => {
            updateConstraintData({
              ...constraintData,
              constraints: {
                ...constraintData.constraints,
                dc_abs_caps: applyTableChanges(
                  withIndexes(constraintData.constraints.dc_abs_caps),
                  addedData,
                  modifiedData,
                  deletedData
                )
              }
            });
          }}
          checkIsValid={checkCapAbsValid}
          selectorOptions={selectorOptions}
        />

        {/* DC Share Caps */}
        <ModalEditWidget<CapConstraint>
          saveOnEdit
          saveButtonText='Apply'
          readOnly={savingConstraint}
          className='constraint-table'
          filterBar={false}
          headers={dcShareCapsHeaders}
          name={
            <span style={{ color: '#7e57c2' }}>
              <MdVerticalAlignTop fill='#7e57c2' size='22' style={{ marginTop: '-2px' }} />
              &nbsp;&nbsp;DC Share Caps
            </span>
          }
          initialTableData={withIndexes(constraintData.constraints.dc_share_caps)}
          onSaveModal={({ addedData, modifiedData, deletedData }) => {
            updateConstraintData({
              ...constraintData,
              constraints: {
                ...constraintData.constraints,
                dc_share_caps: applyTableChanges(
                  withIndexes(constraintData.constraints.dc_share_caps),
                  addedData,
                  modifiedData,
                  deletedData
                )
              }
            });
          }}
          checkIsValid={checkCapShareValid}
          selectorOptions={selectorOptions}
        />

        {/* Global Model Weights */}
        <ModalEditWidget<GlobalDataRow>
          saveOnEdit
          saveButtonText='Apply'
          readOnly={savingConstraint}
          className='constraint-table global-table'
          filterBar={false}
          headers={globalHeaders}
          name={
            <div style={{ color: '#7e57c2', height: '37px' }}>
              <MdSettings fill='#7e57c2' size='22' style={{ marginTop: '-2px' }} />
              &nbsp;&nbsp;Global Model Weights
            </div>
          }
          enableAdd={false}
          enableDelete={false}
          initialTableData={globalData}
          onSaveModal={({ modifiedData }) => {
            const modifiedByKey = new Map(modifiedData.map(row => [row.key, row]));
            const global = globalData
              .map(row => modifiedByKey.get(row.key) ?? row)
              .reduce((acc, row) => ({ ...acc, [row.key]: row.value }), {} as GlobalConstraints);
            updateConstraintData({
              ...constraintData,
              constraints: { ...constraintData.constraints, global }
            });
          }}
          selectorOptions={{}}
        />
      </div>
    </div>
  );
};
